/**
 * Service mapping utilities for converting database service names to display names
 */
import {
  ALL_AVAILABLE_SERVICES,
  SERVICE_CANONICAL_TO_LABEL,
  SERVICE_LABEL_TO_CANONICAL,
} from "../generated/careTaxonomy";
import {
  applyBackendServiceCatalog,
  getActiveReverseServiceMappings,
  getActiveServiceCatalogVersion,
  getActiveServiceNameMappings,
  syncCareTaxonomyFromBackend,
  syncServiceMappingsFromBackend,
} from "./careTaxonomySync";

export {
  applyBackendServiceCatalog,
  getActiveServiceCatalogVersion,
  syncCareTaxonomyFromBackend,
  syncServiceMappingsFromBackend,
};

/**
 * Industry Best Practice: Centralized Service Mapping Service
 *
 * This class provides a centralized way to handle service mappings
 * with proper error handling, logging, and graceful degradation.
 */
export class ServiceMappingService {
  private static instance: ServiceMappingService;
  private displayToDatabaseCache: Map<string, string> = new Map();
  private databaseToDisplayCache: Map<string, string> = new Map();
  private missingServicesLog: string[] = [];
  private serviceUsageStats: Map<string, { count: number; lastUsed: Date }> =
    new Map();
  private serviceAvailabilityStatus: Map<
    string,
    "available" | "missing" | "pending"
  > = new Map();
  private readonly MAX_LOG_SIZE = 1000;

  private constructor() {
    this.initializeCache();
    this.initializeAvailabilityStatus();
  }

  /**
   * Singleton pattern for centralized service management
   */
  public static getInstance(): ServiceMappingService {
    if (!ServiceMappingService.instance) {
      ServiceMappingService.instance = new ServiceMappingService();
    }
    return ServiceMappingService.instance;
  }

  /**
   * Initialize service mapping cache for performance
   * Industry Best Practice: Pre-populate caches for O(1) lookups
   */
  private initializeCache(): void {
    // Pre-populate display to database cache
    Object.entries(getActiveReverseServiceMappings()).forEach(([key, value]) => {
      this.displayToDatabaseCache.set(key, value);
    });

    // Pre-populate database to display cache
    Object.entries(getActiveServiceNameMappings()).forEach(([key, value]) => {
      this.databaseToDisplayCache.set(key, value);
    });
  }

  /**
   * Initialize service availability status
   * Industry Best Practice: Track which services are available vs missing
   */
  private initializeAvailabilityStatus(): void {
    // Mark all services in REVERSE_SERVICE_MAPPINGS as available
    Object.keys(getActiveReverseServiceMappings()).forEach((service) => {
      this.serviceAvailabilityStatus.set(service, "available");
    });
  }

  /**
   * Map frontend display name to database service name
   * Industry Best Practice: Centralized mapping with caching and validation
   */
  public mapToDatabase(displayName: string): string {
    // Input validation
    if (!this.isValidString(displayName)) {
      console.warn(
        "Invalid displayName provided to mapToDatabase:",
        displayName
      );
      return "";
    }

    const mapped = getActiveReverseServiceMappings()[displayName];
    if (mapped) {
      // Cache the result for future lookups
      this.displayToDatabaseCache.set(displayName, mapped);
      return mapped;
    }

    // Fallback to cache for previously-seen passthrough values
    if (this.displayToDatabaseCache.has(displayName)) {
      return this.displayToDatabaseCache.get(displayName)!;
    } else {
      this.logMissingService(displayName, "frontend_to_database");
    }

    return mapped || displayName;
  }

  /**
   * Map database service name to frontend display name
   * Industry Best Practice: Centralized mapping with caching and validation
   */
  public mapToDisplay(serviceCode: string): string {
    // Input validation
    if (!this.isValidString(serviceCode)) {
      console.warn(
        "Invalid serviceCode provided to mapToDisplay:",
        serviceCode
      );
      return "";
    }

    if (this.databaseToDisplayCache.has(serviceCode)) {
      return this.databaseToDisplayCache.get(serviceCode)!;
    }

    const mapped = mapServiceToDisplay(serviceCode);
    this.databaseToDisplayCache.set(serviceCode, mapped);
    if (mapped === serviceCode) {
      this.logMissingService(serviceCode, "database_to_frontend");
    }
    return mapped;
  }

  /**
   * Industry Best Practice: Input validation helper
   */
  private isValidString(value: unknown): value is string {
    return typeof value === "string" && value.trim().length > 0;
  }

  /**
   * Industry Best Practice: Log missing services for future database population
   * with automatic log rotation to prevent memory leaks
   */
  private logMissingService(serviceName: string, direction: string): void {
    const logEntry = `${new Date().toISOString()} - Missing service mapping: "${serviceName}" (${direction})`;

    this.missingServicesLog.push(logEntry);

    // Implement log rotation to prevent memory leaks
    if (this.missingServicesLog.length > this.MAX_LOG_SIZE) {
      // Keep only the most recent entries
      this.missingServicesLog = this.missingServicesLog.slice(
        -this.MAX_LOG_SIZE
      );

      if (process.env.NODE_ENV === "development") {
        console.info(
          `Service mapping log rotated, keeping ${this.MAX_LOG_SIZE} most recent entries`
        );
      }
    }

    // Track service usage and availability
    this.trackServiceUsage(serviceName);
    this.serviceAvailabilityStatus.set(serviceName, "missing");

    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      console.warn(`Service mapping missing: "${serviceName}" (${direction})`);
    }
  }

  /**
   * Industry Best Practice: Track service usage for analytics
   */
  private trackServiceUsage(serviceName: string): void {
    const now = new Date();
    const existing = this.serviceUsageStats.get(serviceName);

    if (existing) {
      existing.count++;
      existing.lastUsed = now;
    } else {
      this.serviceUsageStats.set(serviceName, {
        count: 1,
        lastUsed: now,
      });
    }
  }

  /**
   * Industry Best Practice: Get service usage statistics
   */
  public getServiceUsageStats(): Map<
    string,
    { count: number; lastUsed: Date }
  > {
    return new Map(this.serviceUsageStats);
  }

  /**
   * Industry Best Practice: Get service availability status
   */
  public getServiceAvailabilityStatus(): Map<
    string,
    "available" | "missing" | "pending"
  > {
    return new Map(this.serviceAvailabilityStatus);
  }

  /**
   * Industry Best Practice: Mark service as available (when added to database)
   */
  public markServiceAsAvailable(serviceName: string): void {
    this.serviceAvailabilityStatus.set(serviceName, "available");

    // Log the availability update
    if (process.env.NODE_ENV === "development") {
      console.info(`Service "${serviceName}" marked as available`);
    }
  }

  /**
   * Industry Best Practice: Mark service as pending (planned for database)
   */
  public markServiceAsPending(serviceName: string): void {
    this.serviceAvailabilityStatus.set(serviceName, "pending");

    // Log the pending status
    if (process.env.NODE_ENV === "development") {
      console.info(`Service "${serviceName}" marked as pending`);
    }
  }

  /**
   * Get all missing services for database population planning
   */
  public getMissingServices(): string[] {
    return Array.from(new Set(this.missingServicesLog));
  }

  /**
   * Clear missing services log (useful for testing)
   */
  public clearMissingServicesLog(): void {
    this.missingServicesLog = [];
  }

  /**
   * Industry Best Practice: Get available services for frontend
   */
  public getAvailableServicesForFrontend(): string[] {
    return Object.keys(getActiveReverseServiceMappings());
  }

  /**
   * Industry Best Practice: Validate service selection with helpful feedback
   * Includes comprehensive input validation
   */
  public validateServiceSelection(selectedServices: string[]): {
    isValid: boolean;
    availableServices: string[];
    missingServices: string[];
    suggestions: string[];
    warnings: string[];
  } {
    // Input validation
    if (!Array.isArray(selectedServices)) {
      console.warn(
        "Invalid selectedServices provided to validateServiceSelection:",
        selectedServices
      );
      return {
        isValid: false,
        availableServices: [],
        missingServices: [],
        suggestions: [],
        warnings: ["Invalid input: selectedServices must be an array"],
      };
    }

    const availableServices: string[] = [];
    const missingServices: string[] = [];
    const warnings: string[] = [];

    for (const service of selectedServices) {
      // Validate each service
      if (!this.isValidString(service)) {
        warnings.push(`Invalid service name: "${service}"`);
        continue;
      }

      const mappedService = this.mapToDatabase(service);

      if (
        mappedService === service &&
        !this.displayToDatabaseCache.has(service)
      ) {
        missingServices.push(service);
        warnings.push(
          `Service "${service}" not yet available in database - will be added as providers update their services`
        );
      } else {
        availableServices.push(mappedService);
      }
    }

    // Generate suggestions for missing services
    const suggestions = this.generateSuggestions(missingServices);

    return {
      isValid: missingServices.length === 0,
      availableServices,
      missingServices,
      suggestions,
      warnings,
    };
  }

  /**
   * Industry Best Practice: Generate suggestions for missing services
   */
  private generateSuggestions(missingServices: string[]): string[] {
    const suggestions: string[] = [];

    for (const missing of missingServices) {
      // Find similar services using fuzzy matching
      const similar = Object.keys(getActiveReverseServiceMappings()).filter(
        (service) =>
          this.calculateSimilarity(
            service.toLowerCase(),
            missing.toLowerCase()
          ) > 0.3
      );
      suggestions.push(...similar);
    }

    return Array.from(new Set(suggestions)); // Remove duplicates
  }

  /**
   * Industry Best Practice: Get service recommendations based on usage patterns
   */
  public getServiceRecommendations(requestedServices: string[]): {
    exactMatches: string[];
    similarServices: string[];
    popularServices: string[];
    missingServices: string[];
  } {
    const exactMatches: string[] = [];
    const similarServices: string[] = [];
    const missingServices: string[] = [];

    for (const service of requestedServices) {
      if (getActiveReverseServiceMappings()[service]) {
        exactMatches.push(service);
      } else {
        missingServices.push(service);
        // Find similar services
        const similar = Object.keys(getActiveReverseServiceMappings()).filter(
          (s) =>
            this.calculateSimilarity(s.toLowerCase(), service.toLowerCase()) >
            0.3
        );
        similarServices.push(...similar);
      }
    }

    // Get popular services based on usage stats
    const popularServices = Array.from(this.serviceUsageStats.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 5)
      .map(([service]) => service);

    return {
      exactMatches,
      similarServices: Array.from(new Set(similarServices)),
      popularServices,
      missingServices,
    };
  }

  /**
   * Calculate similarity between two strings (simple implementation)
   */
  private calculateSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1.0;

    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  /**
   * Industry Best Practice: Get comprehensive service mapping report
   */
  public getServiceMappingReport(): {
    totalServices: number;
    availableServices: number;
    missingServices: number;
    pendingServices: number;
    usageStats: Array<{ service: string; count: number; lastUsed: Date }>;
    missingServicesList: string[];
    recommendations: string[];
  } {
    const totalServices = this.serviceAvailabilityStatus.size;
    const availableServices = Array.from(
      this.serviceAvailabilityStatus.values()
    ).filter((status) => status === "available").length;
    const missingServices = Array.from(
      this.serviceAvailabilityStatus.values()
    ).filter((status) => status === "missing").length;
    const pendingServices = Array.from(
      this.serviceAvailabilityStatus.values()
    ).filter((status) => status === "pending").length;

    const usageStats = Array.from(this.serviceUsageStats.entries())
      .map(([service, stats]) => ({ service, ...stats }))
      .sort((a, b) => b.count - a.count);

    const missingServicesList = Array.from(
      this.serviceAvailabilityStatus.entries()
    )
      .filter(([_, status]) => status === "missing")
      .map(([service, _]) => service);

    const recommendations = Array.from(this.serviceUsageStats.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 10)
      .map(([service]) => service);

    return {
      totalServices,
      availableServices,
      missingServices,
      pendingServices,
      usageStats,
      missingServicesList,
      recommendations,
    };
  }

  /**
   * Calculate Levenshtein distance between two strings
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = Array(str2.length + 1)
      .fill(null)
      .map(() => Array(str1.length + 1).fill(null));

    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;

    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1,
          matrix[j - 1][i] + 1,
          matrix[j - 1][i - 1] + indicator
        );
      }
    }

    return matrix[str2.length][str1.length];
  }

  /**
   * Industry Best Practice: Get performance metrics
   */
  public getPerformanceMetrics(): {
    cacheHitRate: number;
    totalLookups: number;
    cacheSize: number;
    memoryUsage: {
      logSize: number;
      maxLogSize: number;
      logUtilization: number;
    };
  } {
    const totalLookups = this.serviceUsageStats.size;
    const cacheSize =
      this.displayToDatabaseCache.size + this.databaseToDisplayCache.size;
    const cacheHitRate =
      totalLookups > 0 ? (cacheSize / totalLookups) * 100 : 0;

    return {
      cacheHitRate: Math.round(cacheHitRate * 100) / 100,
      totalLookups,
      cacheSize,
      memoryUsage: {
        logSize: this.missingServicesLog.length,
        maxLogSize: this.MAX_LOG_SIZE,
        logUtilization:
          (this.missingServicesLog.length / this.MAX_LOG_SIZE) * 100,
      },
    };
  }

  /**
   * Industry Best Practice: Clear caches (useful for testing and memory management)
   */
  public clearCaches(): void {
    this.displayToDatabaseCache.clear();
    this.databaseToDisplayCache.clear();

    if (process.env.NODE_ENV === "development") {
      console.info("Service mapping caches cleared");
    }
  }
}

/**
 * Mapping from database service names to display names used in UI.
 * Base maps are generated from shared/taxonomy/care_taxonomy.json.
 */
export const SERVICE_NAME_MAPPINGS: Record<string, string> = {
  ...SERVICE_CANONICAL_TO_LABEL,
};

/**
 * Reverse mapping from display names to database service names.
 */
export const REVERSE_SERVICE_MAPPINGS: Record<string, string> = {
  ...SERVICE_LABEL_TO_CANONICAL,
};

const SERVICE_LABEL_TO_CANONICAL_LOWER: Record<string, string> = Object.fromEntries(
  Object.entries(SERVICE_LABEL_TO_CANONICAL).map(([label, canonical]) => [
    label.toLowerCase(),
    canonical,
  ])
);

/**
 * Resolve a stored or UI service label to its taxonomy canonical name.
 */
export const resolveServiceLabelToCanonical = (
  label: string
): string | null => {
  const text = (label || "").trim();
  if (!text) return null;

  // service_options wins over runtime capability alias sync for display/dedupe.
  if (text in SERVICE_LABEL_TO_CANONICAL) {
    return SERVICE_LABEL_TO_CANONICAL[text];
  }

  const lower = text.toLowerCase();
  if (lower in SERVICE_LABEL_TO_CANONICAL_LOWER) {
    return SERVICE_LABEL_TO_CANONICAL_LOWER[lower];
  }

  const reverse = getActiveReverseServiceMappings();
  if (text in reverse) {
    return reverse[text];
  }

  for (const [alias, canonical] of Object.entries(reverse)) {
    if (alias.toLowerCase() === lower) {
      return canonical;
    }
  }

  return null;
};

export const mapServiceToDisplay = (serviceName: string): string => {
  const canonical = resolveServiceLabelToCanonical(serviceName);
  if (canonical) {
    const preferred =
      SERVICE_CANONICAL_TO_LABEL[canonical] ||
      getActiveServiceNameMappings()[canonical];
    if (preferred) {
      return preferred;
    }
  }

  return getActiveServiceNameMappings()[serviceName] || serviceName;
};

/**
 * Map services to display labels, collapsing taxonomy aliases that share a canonical.
 */
export const mapServicesToUniqueDisplay = (services: string[]): string[] => {
  const seenCanonical = new Set<string>();
  const unique: string[] = [];

  for (const raw of services) {
    const display = mapServiceToDisplay(raw);
    const canonical =
      resolveServiceLabelToCanonical(raw) ?? display.trim().toLowerCase();
    if (!canonical || seenCanonical.has(canonical)) {
      continue;
    }
    seenCanonical.add(canonical);
    unique.push(display);
  }

  return unique;
};

/** Normalized display key for matching filter aliases to panel checkbox labels. */
export function serviceDisplayKey(serviceName: string): string {
  return mapServiceToDisplay(serviceName).trim().toLowerCase();
}

/** True when any selected value maps to the same display label as the panel option. */
export function serviceSelectionIncludes(
  selected: string[] | undefined,
  panelOptionLabel: string
): boolean {
  const optionKey = serviceDisplayKey(panelOptionLabel);
  return (selected ?? []).some((item) => serviceDisplayKey(item) === optionKey);
}

/** Toggle a care-service panel option, collapsing taxonomy aliases to one panel label. */
export function toggleServiceSelection(
  selected: string[] | undefined,
  panelOptionLabel: string,
  checked: boolean
): string[] {
  const optionKey = serviceDisplayKey(panelOptionLabel);
  const withoutMatch = (selected ?? []).filter(
    (item) => serviceDisplayKey(item) !== optionKey
  );
  if (checked) {
    return [...withoutMatch, panelOptionLabel];
  }
  return withoutMatch;
}

/** Map stored service aliases to exact checkbox panel labels from care taxonomy. */
export function normalizeServiceSelectionsToPanelLabels(
  selected: string[] | undefined
): string[] | undefined {
  if (!selected?.length) {
    return undefined;
  }

  const panelByDisplayKey = new Map(
    ALL_AVAILABLE_SERVICES.map((label) => [serviceDisplayKey(label), label])
  );
  const seen = new Set<string>();
  const result: string[] = [];

  for (const item of selected) {
    const displayKey = serviceDisplayKey(item);
    if (seen.has(displayKey)) {
      continue;
    }
    seen.add(displayKey);
    result.push(panelByDisplayKey.get(displayKey) ?? item);
  }

  return result.length ? result : undefined;
}

/**
 * Map a service name from display format to database format
 * @param displayName - The display name from the UI
 * @returns The database name
 */
export const mapServiceToDatabase = (displayName: string): string => {
  return getActiveReverseServiceMappings()[displayName] || displayName;
};

/**
 * Map an array of service names from database format to display format
 * @param services - Array of service names from the database
 * @returns Array of display names for the UI
 */
export const mapServicesToDisplay = (services: string[]): string[] => {
  return mapServicesToUniqueDisplay(services);
};

/**
 * Map an array of service names from display format to database format
 * @param services - Array of display names from the UI
 * @returns Array of database names
 */
export const mapServicesToDatabase = (services: string[]): string[] => {
  return services.map(mapServiceToDatabase);
};

/**
 * Get all database service names
 * @returns Array of all database service names
 */
export const getDatabaseServiceNames = (): string[] => {
  return Object.keys(getActiveServiceNameMappings());
};

/**
 * Industry Best Practice: Service mapping with graceful degradation
 *
 * This function handles the transition period where frontend services
 * may not yet exist in the database, providing graceful fallbacks.
 *
 * @deprecated Use ServiceMappingService.validateServiceSelection() instead
 */
export const mapServicesToDatabaseWithFallback = (
  frontendServices: string[]
): {
  mappedServices: string[];
  missingServices: string[];
  warnings: string[];
} => {
  const serviceMappingService = ServiceMappingService.getInstance();
  const validation =
    serviceMappingService.validateServiceSelection(frontendServices);

  return {
    mappedServices: validation.availableServices,
    missingServices: validation.missingServices,
    warnings: validation.warnings,
  };
};

/**
 * Industry Best Practice: Get available services for frontend
 *
 * Returns services that are currently available in the database
 * for use in frontend forms and filters.
 *
 * @deprecated Use ServiceMappingService.getAvailableServicesForFrontend() instead
 */
export const getAvailableServicesForFrontend = (): string[] => {
  return ServiceMappingService.getInstance().getAvailableServicesForFrontend();
};

/**
 * Industry Best Practice: Validate service selection
 *
 * Validates that selected services are available and provides
 * helpful feedback for missing services.
 *
 * @deprecated Use ServiceMappingService.validateServiceSelection() instead
 */
export const validateServiceSelection = (
  selectedServices: string[]
): {
  isValid: boolean;
  availableServices: string[];
  missingServices: string[];
  suggestions: string[];
} => {
  const serviceMappingService = ServiceMappingService.getInstance();
  const validation =
    serviceMappingService.validateServiceSelection(selectedServices);

  return {
    isValid: validation.isValid,
    availableServices: validation.availableServices,
    missingServices: validation.missingServices,
    suggestions: validation.suggestions,
  };
};
